"use client";

import { c as _c } from "react/compiler-runtime";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import type { DropInteractionParams } from "../Drop";
import { DropLocation } from "../Drop";
import DropCurationButton from "../DropCurationButton";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropReactions from "../WaveDropReactions";
interface EndedParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function EndedParticipationDrop(t0) {
  const $ = _c(87);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick
  } = t0;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const router = useRouter();
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  let t1;
  if ($[0] !== router) {
    t1 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNavigation = t1;
  let t2;
  if ($[2] !== hasTouch) {
    t2 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[2] = hasTouch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLongPress = t2;
  let t3;
  if ($[4] !== activePartIndex || $[5] !== drop || $[6] !== onReply) {
    t3 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[4] = activePartIndex;
    $[5] = drop;
    $[6] = onReply;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleOnReply = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsSlideUp(false);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleOnAddReaction = t4;
  let t5;
  if ($[9] !== location) {
    t5 = () => {
      if (location === DropLocation.WAVE) {
        return "tw-bg-iron-900/60 tw-ring-1 tw-ring-inset tw-ring-iron-800";
      }
      return "tw-bg-iron-950 tw-ring-1 tw-ring-inset tw-ring-iron-800";
    };
    $[9] = location;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const getDropLocationBackground = t5;
  const dropBackgroundClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getDropLocationBackground();
  const t6 = `${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-w-full`;
  const t7 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-lg tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-linear ${dropBackgroundClass}`;
  let t8;
  if ($[11] !== activePartIndex || $[12] !== drop || $[13] !== handleOnReply || $[14] !== isMobile || $[15] !== showReplyAndQuote) {
    t8 = !isMobile && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[11] = activePartIndex;
    $[12] = drop;
    $[13] = handleOnReply;
    $[14] = isMobile;
    $[15] = showReplyAndQuote;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== drop) {
    t9 = <WaveDropAuthorPfp drop={drop} />;
    $[17] = drop;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== drop.author.level) {
    t10 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[19] = drop.author.level;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== drop.author.handle || $[22] !== drop.author.primary_address || $[23] !== handleNavigation) {
    t11 = e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`);
    $[21] = drop.author.handle;
    $[22] = drop.author.primary_address;
    $[23] = handleNavigation;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = `/${drop.author.handle ?? drop.author.primary_address}`;
  const t13 = drop.author.handle ?? drop.author.primary_address;
  let t14;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={t11} href={t12} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{t13}</Link></p>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== drop.created_at) {
    t16 = getTimeAgoShort(drop.created_at);
    $[30] = drop.created_at;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16) {
    t17 = <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{t16}</p>;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t10 || $[35] !== t14 || $[36] !== t17) {
    t18 = <div className="tw-flex tw-items-center tw-gap-x-2">{t10}{t14}{t15}{t17}</div>;
    $[34] = t10;
    $[35] = t14;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="tw-flex tw-w-fit tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-solid tw-border-iron-500/25 tw-bg-iron-600/10 tw-px-2 tw-py-0.5 tw-font-medium tw-text-iron-500"><span className="tw-text-xs">Participant</span></div>;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18) {
    t20 = <div className="tw-flex tw-flex-col tw-gap-y-2">{t18}{t19}</div>;
    $[39] = t18;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== drop.wave || $[42] !== handleNavigation || $[43] !== showWaveInfo) {
    t21 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={e_1 => handleNavigation(e_1, waveHref)} className="tw-leading-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[41] = drop.wave;
    $[42] = handleNavigation;
    $[43] = showWaveInfo;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== activePartIndex || $[46] !== drop || $[47] !== handleLongPress || $[48] !== hasTouch || $[49] !== onDropContentClick || $[50] !== onQuoteClick) {
    t22 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} />;
    $[45] = activePartIndex;
    $[46] = drop;
    $[47] = handleLongPress;
    $[48] = hasTouch;
    $[49] = onDropContentClick;
    $[50] = onQuoteClick;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t20 || $[53] !== t21 || $[54] !== t22) {
    t23 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t20}{t21}{t22}</div>;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t23 || $[57] !== t9) {
    t24 = <div className="tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t9}{t23}</div>;
    $[56] = t23;
    $[57] = t9;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== drop.metadata) {
    t25 = drop.metadata.length > 0 && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropMetadata metadata={drop.metadata} /></div>;
    $[59] = drop.metadata;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  const t26 = drop.context_profile_context?.curatable ?? false;
  const t27 = drop.context_profile_context?.curated ?? false;
  let t28;
  if ($[61] !== drop.id || $[62] !== drop.wave.id || $[63] !== t26 || $[64] !== t27) {
    t28 = <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={t26} isCurated={t27} />;
    $[61] = drop.id;
    $[62] = drop.wave.id;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== drop) {
    t29 = <WaveDropReactions drop={drop} />;
    $[66] = drop;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t28 || $[69] !== t29) {
    t30 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t28}{t29}</div>;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== drop || $[72] !== handleOnReply || $[73] !== isSlideUp || $[74] !== longPressTriggered || $[75] !== showReplyAndQuote) {
    t31 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[71] = drop;
    $[72] = handleOnReply;
    $[73] = isSlideUp;
    $[74] = longPressTriggered;
    $[75] = showReplyAndQuote;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== t24 || $[78] !== t25 || $[79] !== t30 || $[80] !== t31 || $[81] !== t7 || $[82] !== t8) {
    t32 = <div className={t7}>{t8}{t24}{t25}{t30}{t31}</div>;
    $[77] = t24;
    $[78] = t25;
    $[79] = t30;
    $[80] = t31;
    $[81] = t7;
    $[82] = t8;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== t32 || $[85] !== t6) {
    t33 = <div className={t6}>{t32}</div>;
    $[84] = t32;
    $[85] = t6;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  return t33;
}